import { axios } from 'Core'
import _ from 'lodash'
import { getUserInfo, randomStringGenerator, getCurrentOrg } from 'Utils/Helpers'
import { getSessionToken, SESSION_TOKEN_CACHE } from './sessionToken'
import { PROCYON_SESSION_HEADER_NAME } from 'Utils/Constants'

const validEndpoints = [
  'accesspolicys',
  'access-grants',
  'accounts',
  'accountcheck',
  'appgroups',
  'applications',
  'approles',
  'approvalreqs',
  'approvers',
  'attachments',
  'authzactioncheck',
  'awsresources',
  'azureresources',
  'bookmarks',
  'carts',
  'cloudactions',
  'connectors',
  'credentials',
  'credentialtypes',
  'databases',
  'devices',
  'elasticlog',
  'gcpresources',
  'scm-account',
  'scmresources',
  'googleidproviders',
  'groups',
  'iamactions',
  'idpsecretrefresh',
  'idproviders',
  'jiraintegrations',
  'jiraprojects',
  'jiraissue',
  'kubenamespaces',
  'kafkas',
  'medusanodes',
  'medusanodestates',
  'msTeamsIntegrations',
  'msTeams',
  'msTeamsChannels',
  'nonhumanidentities',
  'notificationsubscriptions',
  'objaccesscheck',
  'objactions',
  'onboardtokens',
  'orgs',
  'pacpolicys',
  'paapolicys',
  'proxies',
  'proxytokens',
  'projects',
  'regions',
  'resourcetypes',
  'samlservers',
  'scimservers',
  'servergroups',
  'servers',
  'servercontrollers',
  'serviceaccounts',
  'sessionmgmt',
  'slackchannels',
  'slackintegrations',
  'sshca',
  'servicenowintegrations',
  'servicenowtables',
  'tagpolicys',
  'targetgroups',
  'targets',
  'tenants',
  'tenantapikeys',
  'tenantprofiles',
  'tenantsiemconfigs',
  'tenantsiemconfigs-healthcheck',
  'tgws',
  'userpreferences',
  'users',
  'vpcs',
  'workloadidentitys',
  'workloadtokens',
  'rdpservers',
  'kubeclusters',
  'visibilitys',
  'privateaccounts',
  'resourcetypes',
  'gsuites',
  'shadowai',
  'shadowaipolicys',
  'secretstoreaccounts',
  'secrets',
  'secrettemplates',
  'cloudagents',
  'aiagentaccounts',
  'agentaccesskeys',
  'aipolicys',
  'aiintents',
  'agentapprovals',
  'agentkeyusage',
  'uservaults'
]

const DefaultNamespaceEndpoints = {
  bookmarks: true,
  carts: true,
  orgs: true,
  users: true,
  devices: true,
  userpreferences: true,
  iamactions: true,
  tenants: true,
  tenantapikeys: true,
  tenantprofiles: true,
  tenantsiemconfigs: true,
  'tenantsiemconfigs-healthcheck': true,
  medusanodes: true,
  proxies: true,
  onboardtokens: true,
  googleidproviders: true,
  idproviders: true,
  idpsecretrefresh: true,
  resourcetypes: true,
  awsresources: true,
  gsuites: true,
  shadowaipolicys: true,
  secretstoreaccounts: true,
  secrets: true,
  secrettemplates: true,
  agentaccesskeys: true,
  aipolicys: true,
  agentapprovals: true,
  agentkeyusage: true
}

/**
 * `reduxApiClient` has been added to handle the redux actions
 * for the API calls. It will automatically update the redux store
 * with the data fetched from the API.
 *
 * Only use ApiProvider for direct API calls that do not require
 * redux actions.
 *
 * @param {import('types').Endpoints} endpoint
 */
export default function ApiProvider(endpoint) {
  if (!validEndpoints.includes(endpoint)) {
    throw new Error('Invalid Enpoint used. Please check the API specs for the correct endpoint')
  }

  // These are constant throughout the app lifecycle
  const user = getUserInfo()
  const { tenant, apiKey, org = getCurrentOrg() } = user
  // Set the API options from existing data
  this.apiOptions = {
    endpoint,
    tenant,
    org,
    version: 'v1',
    config: {}
  }
  this.apiOptions.org = getCurrentOrg()

  this.dataObj = {}

  const config = {
    headers: {
      PROCYON_SESSION_HEADER_NAME: ''
    }
  }

  if (apiKey) {
    config.headers.Authorization = `Bearer ${apiKey}`
  }

  this.apiOptions.config = config

  /**
   * Calls callback if provided, else returns a promise
   * @param {*} methodObject - An axios object which returns a promise
   * @param {*} callback - Callback when request is completed
   * @returns
   */
  const callbackOrPromise = (methodObject, callback = null) => {
    if (typeof callback === 'function') {
      // Use this when method has provided a callback function
      /**
       * After it's resolved the returned value will be in format `response` `error`
       * If there's some error, then response = null, and error will be whatever the response is
       * If there is no errors, response will be response we get and error will be null.
       * error can be checked in `if` statement and get the status of the resolved request
       */
      methodObject
        .then((response) => {
          this.dataObj = response.data
          callback(response, null)
        })
        .catch((error) => {
          callback(null, error)
          if (error.response && error.response.status === 401) {
            // Clear the cached session token on 401 error
            SESSION_TOKEN_CACHE.value = ''
          }
          console.error(error)
        })

      return null
    }

    // Else use a promise. Let the method await
    return new Promise((resolve, reject) => {
      methodObject
        .then((response) => {
          this.dataObj = response.data
          resolve(response)
        })
        .catch(reject)
    })
  }

  /**
   * Sets the instance data for the API endpoint
   * @param {object} dataObj - The data to be updated to the given instance
   * @returns
   */
  this.setInstance = (dataObj) => {
    const toSetDataObj = _.cloneDeep(dataObj)
    if (!toSetDataObj.ObjectMeta) {
      // When ObjectMeta is completely missing
      toSetDataObj.ObjectMeta = {
        Namespace: DefaultNamespaceEndpoints[this.apiOptions.endpoint]
          ? 'default'
          : getCurrentOrg(),
        Tenant: this.apiOptions.tenant
      }
    } else {
      // Override the Namespace and Tenant. They do not change
      toSetDataObj.ObjectMeta.Tenant = this.apiOptions.tenant
      if (!toSetDataObj.ObjectMeta.Namespace) {
        toSetDataObj.ObjectMeta.Namespace = DefaultNamespaceEndpoints[this.apiOptions.endpoint]
          ? 'default'
          : getCurrentOrg()
      }
    }
    this.dataObj = _.merge({}, toSetDataObj)
    return this
  }

  /**
   * Updates the instance data for the API endpoint
   * @param {object} dataObj - The data to be updated to the given instance
   */
  this.updateInstance = (dataObj) => {
    const toSetDataObj = _.cloneDeep(dataObj)
    this.dataObj = _.merge(this.dataObj, toSetDataObj)

    return this
  }
  /**
   * Add unique 6 charater name to the dataobj
   */
  this.addNameToDataObject = () => {
    const uniqueName = randomStringGenerator(6)
    this.dataObj.ObjectMeta.Name = uniqueName
  }

  /**
   * GET All method for the given API endpoint
   * @param {function=} callback - Callback when request is completed
   * @returns
   */
  this.getAll = (callback = null) => {
    const endpoint = this.apiOptions.endpoint

    return new Promise((resolve, reject) => {
      getSessionToken().then((token) => {
        try {
          this.apiOptions.config.headers[PROCYON_SESSION_HEADER_NAME] = token
          const org = getCurrentOrg()
          const params = DefaultNamespaceEndpoints[endpoint] ? '' : `?ObjectMeta.Namespace=${org}`
          const res = callbackOrPromise(
            axios.get(
              `/api/${this.apiOptions.tenant}/${this.apiOptions.version}/${this.apiOptions.endpoint}${params}`,
              this.apiOptions.config
            ),
            callback
          )
          resolve(res)
        } catch (error) {
          reject(error)
        }
      })
    })
  }

  /**
   * GET method for the API endpoint for the set instance
   * @param {*} callback - Callback when request is completed
   * @returns
   */
  this.get = (callback = null) => {
    const name = this.dataObj.ObjectMeta.Name
    var org = this.dataObj.ObjectMeta.Namespace
    if (!org) {
      org = 'default'
    }
    if (!name) {
      throw new Error('API Instance not set correctly. Please refer to the API specs.')
    }

    return new Promise((resolve, reject) => {
      try {
        getSessionToken().then((token) => {
          this.apiOptions.config.headers[PROCYON_SESSION_HEADER_NAME] = token
          const res = callbackOrPromise(
            axios.get(
              `/api/${this.apiOptions.tenant}/${this.apiOptions.version}/${this.apiOptions.endpoint}/${org}/${name}`,
              this.apiOptions.config
            ),
            callback
          )
          resolve(res)
        })
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * POST method for the API endpoint for new data
   * @param {*} callback - Callback when request is completed
   * @returns
   */
  this.post = (callback = null, generateName = false) => {
    return new Promise((resolve, reject) => {
      getSessionToken().then((token) => {
        try {
          if (generateName) this.addNameToDataObject()
          const endpoint = this.apiOptions.endpoint
          // This will overwrite the default namespace for all the create request
          this.dataObj.ObjectMeta.Namespace = DefaultNamespaceEndpoints[endpoint]
            ? 'default'
            : getCurrentOrg()

          this.apiOptions.config.headers[PROCYON_SESSION_HEADER_NAME] = token

          const res = callbackOrPromise(
            axios.post(
              `/api/${this.apiOptions.tenant}/${this.apiOptions.version}/${this.apiOptions.endpoint}`,
              this.dataObj,
              this.apiOptions.config
            ),
            callback
          )
          resolve(res)
        } catch (error) {
          reject(error)
        }
      })
    })
  }

  /**
   * PUT method for the API endpoint to update a set instance
   * @param {*} callback - Callback when request is completed
   * @returns
   */
  this.put = (callback = null) => {
    return new Promise((resolve, reject) => {
      try {
        getSessionToken().then((token) => {
          const name = this.dataObj.ObjectMeta.Name
          const org = this.dataObj.ObjectMeta.Namespace

          if (!name) {
            throw new Error('API Instance not set correctly. Please refer to the API specs.')
          }

          this.apiOptions.config.headers[PROCYON_SESSION_HEADER_NAME] = token

          const res = callbackOrPromise(
            axios.put(
              `/api/${this.apiOptions.tenant}/${this.apiOptions.version}/${this.apiOptions.endpoint}/${org}/${name}`,
              this.dataObj,
              this.apiOptions.config
            ),
            callback
          )
          resolve(res)
        })
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * DELETE method for the API endpoint to delete the set instance
   * @param {*} callback
   * @returns
   */
  this.delete = (callback = null) => {
    return new Promise((resolve, reject) => {
      getSessionToken().then((token) => {
        try {
          const name = this.dataObj.ObjectMeta?.Name
          const org = this.dataObj.ObjectMeta?.Namespace
          this.apiOptions.config.headers[PROCYON_SESSION_HEADER_NAME] = token

          if (!name) {
            throw new Error('API Instance not set correctly. Please refer to the API specs.')
          }

          const res = callbackOrPromise(
            axios.delete(
              `/api/${this.apiOptions.tenant}/${this.apiOptions.version}/${this.apiOptions.endpoint}/${org}/${name}`,
              this.apiOptions.config
            ),
            callback
          )
          resolve(res)
        } catch (error) {
          reject(error)
        }
      })
    })
  }

  /**
   * GET by Param
   *
   */

  this.getByParam = (callback = null) => {
    return new Promise(() => {
      getSessionToken().then((token) => {
        const param = `ObjectMeta.Namespace=${this?.dataObj?.ObjectMeta?.Namespace}`
        this.apiOptions.config.headers[PROCYON_SESSION_HEADER_NAME] = token
        if (!param) {
          throw new Error('Param is not found in NameSpace')
        }
        return callbackOrPromise(
          axios.get(
            `/api/${this.apiOptions.tenant}/${this.apiOptions.version}/${this.apiOptions.endpoint}?${param}`,
            this.apiOptions.config
          ),
          callback
        )
      })
    })
  }

  /**
   *
   * @param {import('axios').AxiosRequestConfig} options
   */
  this.updateApiOptions = (options = {}) => {
    this.apiOptions.config = { ...this.apiOptions.config, ...options }
    return this
  }
}

// const myCallback = (response) => {
//   console.log('This is my callback', response.data)
// }

// Get all accounts
// const accounts = new ApiProvider('accounts').getAll(myCallback)

// Get one account
// const currentAccountProvider = new ApiProvider('accounts').setInstance(singleAccountObj)
// const currentAccountData = currentAccountProvider.get(myCallback)

// // To delete
// currentAccountProvider.delete()

// // To update (put)
// currentAccountProvider.updateInstance({ Spec: { somekey: 'someNewValue' } })

// currentAccountProvider.updateInstance({ Spec: { someotherKey: 'someOtherNewValue' } })

// onSubmit = () => {
//   currentAccountProvider.put()
// }

// // New account
// newAccountDetails = {
//   AWSKey: 'some',
//   ObjectMeta: {
//     Name: 'SomeName'
//   }
// }
